import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "@/lib/api";
import {
  Edit3, Save, X, Plus, Trash2, RefreshCcw, Eye, EyeOff,
  Filter as FilterIcon, ChevronUp, ChevronDown,
} from "lucide-react";

const DATA_TYPES = [
  { v: "text", l: "Text" },
  { v: "number", l: "Number" },
  { v: "currency", l: "Currency" },
  { v: "option", l: "Option (single)" },
  { v: "multi_option", l: "Option (multi)" },
  { v: "boolean", l: "Boolean" },
  { v: "date", l: "Date" },
];
const FILTER_UIS = [
  { v: "", l: "—" },
  { v: "range", l: "Range" },
  { v: "multi_select", l: "Multi-select" },
  { v: "single_select", l: "Single select" },
  { v: "toggle", l: "Toggle" },
  { v: "date_range", l: "Date range" },
  { v: "search", l: "Search" },
];

/**
 * FieldsTab — Smart Unit Finder SF-1 admin configurator.
 *
 * Admin selects which entity fields are filterable / visible / typed, and
 * how they behave in the Unit Finder. The list is auto-bootstrapped from
 * existing entity columns + `entity.fields` keys on first open.
 */
export default function FieldsTab({ pid, project }) {
  const [items, setItems] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/projects/${pid}/field-definitions`);
      setItems(data);
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  useEffect(() => { load(); }, [pid]);

  const startEdit = (fd) => {
    setEditingId(fd.id);
    setForm({ ...fd });
    setCreating(false);
  };
  const startCreate = () => {
    setCreating(true);
    setEditingId(null);
    setForm({
      project_id: pid, key: "", label: "",
      data_type: "text", filter_ui: "", filterable: false,
      public_visible: true, client_editable: true,
      card_visible: false, detail_visible: true, compare_visible: true, pdf_visible: true,
      auto_options: true, options: [], aliases: [],
      canonical_source: "fields",
    });
  };

  const save = async () => {
    try {
      if (creating) {
        await api.post("/admin/field-definitions", form);
        toast.success("Field created");
      } else if (editingId) {
        await api.put(`/admin/field-definitions/${editingId}`, form);
        toast.success("Field saved");
      }
      setCreating(false); setEditingId(null); setForm(null);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const remove = async (fd) => {
    if (!confirm(`Delete field "${fd.label}"?`)) return;
    try {
      await api.delete(`/admin/field-definitions/${fd.id}`);
      toast.success("Field removed");
      await load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const recalc = async (fd) => {
    try {
      await api.post(`/admin/field-definitions/${fd.id}/recalc`);
      toast.success(`Recalculated "${fd.label}"`);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const reorder = async (idx, dir) => {
    const next = items.slice();
    const j = idx + (dir === "up" ? -1 : 1);
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setItems(next);
    try {
      await Promise.all(next.map((fd, k) => api.put(`/admin/field-definitions/${fd.id}`, { filter_order: k })));
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  return (
    <div className="space-y-6" data-testid="fields-tab">
      <PresetsSection pid={pid} />
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium">Field Settings</h2>
          <p className="text-sm text-zinc-500 mt-0.5">
            These definitions drive the Smart Unit Finder, unit cards, Compare and PDF — including filter ranges,
            multi-select options and translations.
          </p>
        </div>
        <button onClick={startCreate} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-zinc-900 text-white text-sm hover:bg-zinc-700" data-testid="fields-create-btn">
          <Plus className="h-4 w-4" /> New field
        </button>
      </div>

      {creating || editingId ? (
        <FieldForm
          supported={(project?.supported_languages || ["en", "ar", "tr", "ru"]).filter(l => l !== (project?.default_language || "en"))}
          form={form} setForm={setForm}
          onSave={save}
          onCancel={() => { setCreating(false); setEditingId(null); setForm(null); }}
        />
      ) : null}

      <div className="border border-zinc-200 rounded-xl bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-zinc-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-3 py-2">Field</th>
              <th className="text-left px-3 py-2">Type</th>
              <th className="text-left px-3 py-2">Filter UI</th>
              <th className="text-center px-3 py-2">Filterable</th>
              <th className="text-center px-3 py-2">Card</th>
              <th className="text-center px-3 py-2">Detail</th>
              <th className="text-center px-3 py-2">PDF</th>
              <th className="text-left px-3 py-2">Range / Options</th>
              <th className="px-3 py-2 w-1" />
            </tr>
          </thead>
          <tbody>
            {items.map((fd, idx) => (
              <tr key={fd.id} className="border-t border-zinc-100" data-testid={`fields-row-${fd.key}`}>
                <td className="px-3 py-2">
                  <div className="font-medium">{fd.label}</div>
                  <div className="text-[11px] text-zinc-500">key: <code>{fd.key}</code> · src: {fd.canonical_source}</div>
                </td>
                <td className="px-3 py-2">{fd.data_type}{fd.unit ? <span className="text-zinc-400 ml-1">({fd.unit})</span> : null}</td>
                <td className="px-3 py-2">{fd.filter_ui || <span className="text-zinc-400">—</span>}</td>
                <td className="px-3 py-2 text-center">{fd.filterable ? <FilterIcon className="h-4 w-4 inline text-emerald-600" /> : <span className="text-zinc-300">—</span>}</td>
                <td className="px-3 py-2 text-center">{fd.card_visible ? <Eye className="h-4 w-4 inline text-emerald-600" /> : <EyeOff className="h-4 w-4 inline text-zinc-300" />}</td>
                <td className="px-3 py-2 text-center">{fd.detail_visible ? <Eye className="h-4 w-4 inline text-emerald-600" /> : <EyeOff className="h-4 w-4 inline text-zinc-300" />}</td>
                <td className="px-3 py-2 text-center">{fd.pdf_visible ? <Eye className="h-4 w-4 inline text-emerald-600" /> : <EyeOff className="h-4 w-4 inline text-zinc-300" />}</td>
                <td className="px-3 py-2">
                  {fd.data_type === "number" || fd.data_type === "currency"
                    ? <span className="text-xs text-zinc-600">{fd.min_value ?? "—"} → {fd.max_value ?? "—"}</span>
                    : (fd.options && fd.options.length > 0)
                      ? <span className="text-xs text-zinc-600">{fd.options.slice(0, 5).join(", ")}{fd.options.length > 5 ? "…" : ""}</span>
                      : <span className="text-xs text-zinc-400">—</span>}
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <div className="inline-flex items-center gap-0.5">
                    <button onClick={() => reorder(idx, "up")} className="p-1 hover:bg-zinc-100 rounded text-zinc-500" title="Move up" data-testid={`fields-up-${fd.key}`}><ChevronUp className="h-4 w-4" /></button>
                    <button onClick={() => reorder(idx, "down")} className="p-1 hover:bg-zinc-100 rounded text-zinc-500" title="Move down" data-testid={`fields-down-${fd.key}`}><ChevronDown className="h-4 w-4" /></button>
                    <button onClick={() => recalc(fd)} className="p-1 hover:bg-zinc-100 rounded text-zinc-500" title="Recalculate min/max + auto-options" data-testid={`fields-recalc-${fd.key}`}><RefreshCcw className="h-4 w-4" /></button>
                    <button onClick={() => startEdit(fd)} className="p-1 hover:bg-zinc-100 rounded text-zinc-700" title="Edit" data-testid={`fields-edit-${fd.key}`}><Edit3 className="h-4 w-4" /></button>
                    <button onClick={() => remove(fd)} className="p-1 hover:bg-red-50 rounded text-red-600" title="Delete" data-testid={`fields-delete-${fd.key}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={9} className="px-3 py-6 text-center text-sm text-zinc-500">No fields yet. Click <strong>New field</strong> or add units to auto-seed defaults.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FieldForm({ supported, form, setForm, onSave, onCancel }) {
  if (!form) return null;
  const set = (patch) => setForm({ ...form, ...patch });
  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 space-y-3" data-testid="fields-form">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Field label="Label">
          <input type="text" value={form.label} onChange={(e) => set({ label: e.target.value })}
            className="w-full px-3 py-2 rounded-md border border-zinc-300 text-sm" data-testid="fields-form-label" />
        </Field>
        {(supported || []).map(lang => (
          <Field key={lang} label={`Label (${lang})`}>
            <input aria-label={`Label (${lang})`} lang={lang} dir={lang === "ar" ? "rtl" : "ltr"} type="text" value={form.translations?.[lang] || ""}
              onChange={e => set({ translations: { ...(form.translations || {}), [lang]: e.target.value } })}
              className="w-full px-3 py-2 rounded-md border border-zinc-300 text-sm" data-testid={`fields-form-label-${lang}`} />
          </Field>
        ))}
        <Field label="Key (slug)">
          <input type="text" value={form.key} onChange={(e) => set({ key: e.target.value })}
            className="w-full px-3 py-2 rounded-md border border-zinc-300 text-sm font-mono" data-testid="fields-form-key" />
        </Field>
        <Field label="Data type">
          <select value={form.data_type} onChange={(e) => set({ data_type: e.target.value })}
            className="w-full px-3 py-2 rounded-md border border-zinc-300 text-sm" data-testid="fields-form-type">
            {DATA_TYPES.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
        </Field>
        <Field label="Filter UI">
          <select value={form.filter_ui || ""} onChange={(e) => set({ filter_ui: e.target.value || null })}
            className="w-full px-3 py-2 rounded-md border border-zinc-300 text-sm" data-testid="fields-form-filter-ui">
            {FILTER_UIS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
        </Field>
        <Field label="Unit (e.g. m²)">
          <input type="text" value={form.unit || ""} onChange={(e) => set({ unit: e.target.value || null })}
            className="w-full px-3 py-2 rounded-md border border-zinc-300 text-sm" data-testid="fields-form-unit" />
        </Field>
        <Field label="Currency (ISO)">
          <input type="text" value={form.currency || ""} onChange={(e) => set({ currency: e.target.value || null })}
            className="w-full px-3 py-2 rounded-md border border-zinc-300 text-sm font-mono" placeholder="USD" />
        </Field>
        <Field label="Canonical source">
          <select value={form.canonical_source} onChange={(e) => set({ canonical_source: e.target.value })}
            className="w-full px-3 py-2 rounded-md border border-zinc-300 text-sm">
            <option value="fields">Entity fields bag</option>
            <option value="top_level">Top-level column</option>
          </select>
        </Field>
        <Field label="Aliases (comma-sep.)">
          <input type="text" value={(form.aliases || []).join(", ")}
            onChange={(e) => set({ aliases: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
            className="w-full px-3 py-2 rounded-md border border-zinc-300 text-sm" />
        </Field>
      </div>
      {(form.data_type === "option" || form.data_type === "multi_option") && (
        <Field label="Options (comma-sep — leave empty for auto)">
          <input type="text" value={(form.options || []).join(", ")}
            onChange={(e) => set({ options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean), auto_options: false })}
            className="w-full px-3 py-2 rounded-md border border-zinc-300 text-sm" data-testid="fields-form-options" />
        </Field>
      )}
      <div className="grid grid-cols-3 md:grid-cols-7 gap-2">
        <Toggle label="Filterable" v={form.filterable} on={(b) => set({ filterable: b })} />
        <Toggle label="Public" v={form.public_visible} on={(b) => set({ public_visible: b })} />
        <Toggle label="Card" v={form.card_visible} on={(b) => set({ card_visible: b })} />
        <Toggle label="Detail" v={form.detail_visible} on={(b) => set({ detail_visible: b })} />
        <Toggle label="Compare" v={form.compare_visible} on={(b) => set({ compare_visible: b })} />
        <Toggle label="PDF" v={form.pdf_visible} on={(b) => set({ pdf_visible: b })} />
        <Toggle label="Client edit" v={form.client_editable} on={(b) => set({ client_editable: b })} />
      </div>
      <div className="flex gap-2 pt-1">
        <button onClick={onSave} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-zinc-900 text-white text-sm hover:bg-zinc-700" data-testid="fields-form-save">
          <Save className="h-4 w-4" /> Save
        </button>
        <button onClick={onCancel} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-zinc-300 bg-white text-sm hover:bg-zinc-50" data-testid="fields-form-cancel">
          <X className="h-4 w-4" /> Cancel
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-xs text-zinc-700 mb-1.5">{label}</div>
      {children}
    </div>
  );
}
function Toggle({ label, v, on }) {
  return (
    <label className="inline-flex items-center gap-2 text-xs cursor-pointer select-none px-2 py-1.5 rounded border border-zinc-200 bg-white">
      <input type="checkbox" checked={Boolean(v)} onChange={(e) => on(e.target.checked)} className="accent-amber-600" />
      {label}
    </label>
  );
}


// ── Filter Presets section (SF-3) ────────────────────────────────────────
function PresetsSection({ pid }) {
  const [items, setItems] = React.useState([]);
  const [fields, setFields] = React.useState([]);
  const [creating, setCreating] = React.useState(false);
  const [editingId, setEditingId] = React.useState(null);
  const [form, setForm] = React.useState(null);

  const load = async () => {
    try {
      const [p, f] = await Promise.all([
        api.get(`/admin/projects/${pid}/filter-presets`),
        api.get(`/admin/projects/${pid}/field-definitions`),
      ]);
      setItems(p.data);
      setFields(f.data.filter((x) => x.filterable));
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  React.useEffect(() => { load(); }, [pid]);   // eslint-disable-line react-hooks/exhaustive-deps

  const startCreate = () => {
    setCreating(true); setEditingId(null);
    setForm({ project_id: pid, name: "", filters: {}, visibility: true, sort_order: items.length });
  };
  const startEdit = (p) => {
    setEditingId(p.id); setCreating(false);
    setForm({ ...p });
  };
  const save = async () => {
    try {
      if (creating) {
        await api.post("/admin/filter-presets", form);
        toast.success("Preset saved");
      } else if (editingId) {
        await api.put(`/admin/filter-presets/${editingId}`, form);
        toast.success("Preset updated");
      }
      setCreating(false); setEditingId(null); setForm(null);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const remove = async (p) => {
    if (!confirm(`Delete preset "${p.name}"?`)) return;
    try {
      await api.delete(`/admin/filter-presets/${p.id}`);
      toast.success("Preset removed");
      await load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const editFilter = (key, value) => setForm({ ...form, filters: { ...(form.filters || {}), [key]: value } });
  const dropFilter = (key) => {
    const next = { ...(form?.filters || {}) };
    delete next[key];
    setForm({ ...form, filters: next });
  };

  return (
    <div className="space-y-3" data-testid="presets-section">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium">Saved Filter Presets</h2>
          <p className="text-sm text-zinc-500 mt-0.5">
            One-click filter chips that appear in the buyer's Smart Unit Finder. Useful for sales scripts like "Sea View 2+1" or "Investment under 150K".
          </p>
        </div>
        <button onClick={startCreate} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-zinc-900 text-white text-sm hover:bg-zinc-700" data-testid="presets-create-btn">
          <Plus className="h-4 w-4" /> New preset
        </button>
      </div>
      {(creating || editingId) && form && (
        <PresetForm form={form} setForm={setForm} fields={fields} onSave={save}
          editFilter={editFilter} dropFilter={dropFilter}
          onCancel={() => { setCreating(false); setEditingId(null); setForm(null); }} />
      )}
      <div className="border border-zinc-200 rounded-xl bg-white overflow-hidden">
        {items.length === 0 ? (
          <div className="px-4 py-6 text-sm text-zinc-500 text-center">
            No presets yet. Click <strong>New preset</strong> to add one.
          </div>
        ) : (
          <ul className="divide-y divide-zinc-100">
            {items.map((p) => (
              <li key={p.id} className="flex items-center gap-3 px-3 py-2" data-testid={`preset-row-${p.id}`}>
                <div className="flex-1">
                  <div className="font-medium">{p.name}</div>
                  <div className="text-[11px] text-zinc-500 truncate">
                    {Object.entries(p.filters || {}).map(([k, v]) => (
                      <span key={k} className="inline-block me-2">
                        <code className="text-[10px]">{k}</code>: <span className="text-zinc-700">{Array.isArray(v) ? v.join(", ") : typeof v === "object" ? `${v.min ?? "*"}–${v.max ?? "*"}` : String(v)}</span>
                      </span>
                    ))}
                  </div>
                </div>
                {!p.visibility && <span className="text-[10px] uppercase tracking-wider text-zinc-400 px-2">hidden</span>}
                <button onClick={() => startEdit(p)} className="p-1.5 hover:bg-zinc-100 rounded" data-testid={`preset-edit-${p.id}`}><Edit3 className="h-4 w-4" /></button>
                <button onClick={() => remove(p)} className="p-1.5 hover:bg-red-50 rounded text-red-600" data-testid={`preset-delete-${p.id}`}><Trash2 className="h-4 w-4" /></button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function PresetForm({ form, setForm, fields, editFilter, dropFilter, onSave, onCancel }) {
  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 space-y-3" data-testid="preset-form">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Field label="Name">
          <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full px-3 py-2 rounded-md border border-zinc-300 text-sm" data-testid="preset-form-name" />
        </Field>
        <Field label="Sort order">
          <input type="number" value={form.sort_order || 0} onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value, 10) || 0 })}
            className="w-full px-3 py-2 rounded-md border border-zinc-300 text-sm" />
        </Field>
        <Field label="Visible">
          <select value={form.visibility ? "1" : "0"} onChange={(e) => setForm({ ...form, visibility: e.target.value === "1" })}
            className="w-full px-3 py-2 rounded-md border border-zinc-300 text-sm">
            <option value="1">Public</option>
            <option value="0">Hidden</option>
          </select>
        </Field>
      </div>
      <div>
        <div className="text-xs text-zinc-700 mb-2">Filters</div>
        <div className="space-y-2">
          {Object.entries(form.filters || {}).map(([k, v]) => (
            <div key={k} className="flex items-center gap-2 bg-white rounded-md border border-zinc-200 px-2 py-1.5 text-xs">
              <code className="font-medium">{k}</code>
              <span className="text-zinc-500 flex-1">
                {Array.isArray(v) ? v.join(", ") : typeof v === "object" ? `min=${v.min ?? "*"} max=${v.max ?? "*"}` : String(v)}
              </span>
              <button onClick={() => dropFilter(k)} className="p-1 text-red-600 hover:bg-red-50 rounded"><X className="h-3 w-3" /></button>
            </div>
          ))}
        </div>
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
          {fields.map((fd) => (
            <FilterBuilderPill key={fd.key} fd={fd} value={form.filters?.[fd.key]} onChange={(v) => editFilter(fd.key, v)} />
          ))}
        </div>
      </div>
      <div className="flex gap-2 pt-1">
        <button onClick={onSave} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-zinc-900 text-white text-sm hover:bg-zinc-700" data-testid="preset-form-save">
          <Save className="h-4 w-4" /> Save
        </button>
        <button onClick={onCancel} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-zinc-300 bg-white text-sm hover:bg-zinc-50" data-testid="preset-form-cancel">
          <X className="h-4 w-4" /> Cancel
        </button>
      </div>
    </div>
  );
}

function FilterBuilderPill({ fd, value, onChange }) {
  const [open, setOpen] = React.useState(false);
  const has = value != null && !(Array.isArray(value) && value.length === 0);
  return (
    <div className="relative">
      <button onClick={() => setOpen((x) => !x)}
        className={`w-full text-start px-2.5 py-1.5 rounded-md border text-xs ${has ? "bg-amber-100 border-amber-300 text-amber-900" : "bg-white border-zinc-200 hover:bg-zinc-50"}`}>
        {fd.label} {has ? "•" : "+"}
      </button>
      {open && (
        <div className="absolute z-10 mt-1 min-w-[220px] bg-white border border-zinc-200 rounded-md shadow-lg p-3" onMouseLeave={() => setOpen(false)}>
          {fd.filter_ui === "multi_select" ? (
            <div className="flex flex-wrap gap-1">
              {(fd.options || []).map((o) => {
                const arr = value || [];
                const on = arr.includes(o);
                return (
                  <button key={o} onClick={() => onChange(on ? arr.filter((x) => x !== o) : [...arr, o])}
                    className={`px-2 py-1 rounded-full text-xs border ${on ? "bg-zinc-900 text-white border-zinc-900" : "bg-white border-zinc-200"}`}>
                    {o}
                  </button>
                );
              })}
            </div>
          ) : fd.filter_ui === "range" ? (
            <div className="grid grid-cols-2 gap-1">
              <input type="number" placeholder={`min ${fd.min_value ?? ""}`} value={value?.min ?? ""}
                onChange={(e) => onChange({ ...(value || {}), min: e.target.value === "" ? null : Number(e.target.value) })}
                className="px-2 py-1.5 rounded border border-zinc-300 text-xs" />
              <input type="number" placeholder={`max ${fd.max_value ?? ""}`} value={value?.max ?? ""}
                onChange={(e) => onChange({ ...(value || {}), max: e.target.value === "" ? null : Number(e.target.value) })}
                className="px-2 py-1.5 rounded border border-zinc-300 text-xs" />
            </div>
          ) : fd.filter_ui === "toggle" ? (
            <div className="flex gap-1">
              <button onClick={() => onChange(null)}  className={`px-2 py-1 rounded text-xs ${value == null ? "bg-zinc-900 text-white" : "bg-white border border-zinc-200"}`}>Any</button>
              <button onClick={() => onChange(true)}  className={`px-2 py-1 rounded text-xs ${value === true ? "bg-zinc-900 text-white" : "bg-white border border-zinc-200"}`}>Yes</button>
              <button onClick={() => onChange(false)} className={`px-2 py-1 rounded text-xs ${value === false ? "bg-zinc-900 text-white" : "bg-white border border-zinc-200"}`}>No</button>
            </div>
          ) : (
            <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)}
              className="w-full px-2 py-1.5 rounded border border-zinc-300 text-xs" placeholder="Value" />
          )}
        </div>
      )}
    </div>
  );
}

